import React from 'react';
import { Shield, AlertTriangle, Scale } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import FloatingButtons from '../components/common/FloatingButtons';

const TermsConditionsPage = () => {
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section 
        className="relative h-[40vh] flex items-center justify-center"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/685a4a49-d889-47d7-87c9-a5d959d86d4d/images/03bc2d0dc3cc6d96499df8744f03df229c3d5db1ac26862b5bd7f220688e5909.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 text-center text-white px-6">
          <Scale className="w-16 h-16 mx-auto mb-4 text-teal-400" />
          <h1 className="text-4xl sm:text-5xl font-bold mb-2">Terms & Conditions</h1>
          <p className="text-lg text-gray-200">Last updated: February 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="prose prose-lg max-w-none">
          
          {/* Important Notice */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-8 rounded-r-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-amber-800 font-semibold m-0 mb-2">Important Notice</h3>
                <p className="text-amber-700 m-0">
                  <strong>Doktor Gigi is NOT a hospital and does NOT provide medical treatment.</strong> We operate solely as an information and doctor-connect platform.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. About Doktor Gigi</h2>
          <p className="text-gray-600 mb-4">
            Doktor Gigi ("we," "us," or "our") is an online platform that provides:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
            <li>Educational information about kidney health and related conditions</li>
            <li>A platform to connect patients with qualified kidney specialists</li>
            <li>Consultation booking services</li>
          </ul>
          <p className="text-gray-600 mb-6">
            <strong>We do NOT:</strong>
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
            <li>Operate as a hospital, clinic, or medical facility</li>
            <li>Provide direct medical treatment, diagnosis, or prescriptions</li>
            <li>Replace professional medical consultation or emergency services</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Use of Services</h2>
          <p className="text-gray-600 mb-4">
            By using our platform, you agree that:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
            <li>You are at least 18 years of age or have parental/guardian consent</li>
            <li>The information you provide is accurate and complete</li>
            <li>You will not misuse our services for any unlawful purpose</li>
            <li>Any information obtained through our platform is for educational purposes only</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Doctor Connection Services</h2>
          <p className="text-gray-600 mb-6">
            Our platform facilitates connections between patients and registered medical practitioners. However:
          </p>
          <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
            <li>We do not employ the doctors listed on our platform</li>
            <li>Medical professionals are independent practitioners responsible for their own services</li>
            <li>Any medical advice, diagnosis, or treatment is provided by the doctor, not by Doktor Gigi</li>
            <li>We do not guarantee the availability or quality of any specific doctor</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Limitation of Liability</h2>
          <div className="bg-gray-50 p-6 rounded-lg mb-6">
            <p className="text-gray-700 mb-4">
              <strong>To the maximum extent permitted by law, Doktor Gigi shall not be liable for:</strong>
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Any direct, indirect, incidental, special, or consequential damages arising from the use of our platform</li>
              <li>Any medical outcomes resulting from consultations arranged through our platform</li>
              <li>Any errors, omissions, or inaccuracies in the information provided</li>
              <li>Any loss or damage caused by reliance on information obtained through our services</li>
              <li>Any technical failures, interruptions, or security breaches</li>
            </ul>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Indemnification</h2>
          <p className="text-gray-600 mb-6">
            You agree to indemnify and hold harmless Doktor Gigi, its directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of our platform or violation of these terms.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Intellectual Property</h2>
          <p className="text-gray-600 mb-6">
            All content on our platform, including text, graphics, logos, and images, is the property of Doktor Gigi and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our written consent.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Modifications to Terms</h2>
          <p className="text-gray-600 mb-6">
            We reserve the right to modify these Terms & Conditions at any time. Changes will be effective immediately upon posting on our website. Your continued use of our services constitutes acceptance of the modified terms.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Dispute Resolution & Jurisdiction</h2>
          <div className="bg-teal-50 p-6 rounded-lg mb-6">
            <p className="text-gray-700 mb-4">
              <strong>Governing Law:</strong> These Terms & Conditions shall be governed by and construed in accordance with the laws of India.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Jurisdiction:</strong> Any disputes arising from these terms or your use of our services shall be subject to the exclusive jurisdiction of the courts in [City], India.
            </p>
            <p className="text-gray-700">
              <strong>Dispute Resolution:</strong> Before initiating any legal proceedings, parties agree to attempt resolution through good-faith negotiation and, if necessary, mediation.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Contact Us</h2>
          <p className="text-gray-600 mb-4">
            For questions about these Terms & Conditions, please contact us at:
          </p>
          <p className="text-gray-600">
            <strong>Email:</strong> doktorgigi4@gmail.com
          </p>
        </div>
      </section>
    </div>
    <Footer />
    <FloatingButtons />
    </>
  );
};

export default TermsConditionsPage;
